import React, { createContext, useContext, useState, useEffect } from 'react';
import { logout as apiLogout, setOnLogoutCallback } from '../utils/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessToken, setAccessToken] = useState(null);

  useEffect(() => {
    // Check for stored tokens on mount
    const storedAccessToken = localStorage.getItem('accessToken');
    const storedRefreshToken = localStorage.getItem('refreshToken');
    const storedUser = localStorage.getItem('user');

    if (storedAccessToken && storedUser) {
      setAccessToken(storedAccessToken);
      setUser(JSON.parse(storedUser));
    }

    setLoading(false);
  }, []);

  // NOTE: previous behavior attempted to log the user out on every page unload
  // (beforeunload/unload). That caused users to be logged out when the app
  // navigated or performed full-page navigations (e.g. window.location.href).
  // To avoid unexpected logouts during normal navigation, we no longer clear
  // localStorage or call logout on unload. Rely on explicit `logout()` calls
  // (user action) or token expiry/refresh for session lifecycle.

  const login = (accessToken, refreshToken, userData) => {
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('user', JSON.stringify(userData));
    setAccessToken(accessToken);
    setUser(userData);
  };

  const logout = async () => {
    // Clear React state immediately
    setAccessToken(null);
    setUser(null);
    
    // Sign out from Firebase if initialized
    try {
      const { signOut } = await import('firebase/auth');
      const auth = (await import('../config/firebase')).default;
      if (auth) {
        await signOut(auth);
        console.log('✅ Firebase sign out successful');
      }
    } catch (error) {
      console.log('Firebase sign out skipped (not initialized or error):', error.message);
    }
    
    // Call the centralized logout from api.js
    // (it will clear storage, call backend API, and redirect)
    await apiLogout();
  };

  // Register state cleanup callback with api utility
  useEffect(() => {
    setOnLogoutCallback(() => {
      setAccessToken(null);
      setUser(null);
    });
  }, []);

  const refreshAccessToken = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      
      const response = await fetch('/api/auth/token/refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ refreshToken })
      });

      const data = await response.json();

      if (data.success) {
        localStorage.setItem('accessToken', data.data.accessToken);
        setAccessToken(data.data.accessToken);
        return data.data.accessToken;
      } else {
        logout();
        return null;
      }
    } catch (error) {
      console.error('Token refresh error:', error);
      logout();
      return null;
    }
  };

  const hasRole = (allowedRoles) => {
    if (!user) return false;
    return allowedRoles.includes(user.role);
  };

  const value = {
    user,
    accessToken,
    loading,
    login,
    logout,
    refreshAccessToken,
    hasRole,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
